/**
 * Quotation PDF export and shared PDF open/download helpers.
 * Uses the backend PDF endpoint (letterhead + content). Pass the quotation group id.
 *
 * Reference data shape (for building from API response if needed):
 * @typedef {Object} QuotationData
 * @property {string} quotationNo - e.g. "EQ-2026-0042"
 * @property {string} date - e.g. "27 Feb 2026"
 * @property {string} customerName
 * @property {string} [customerAddress]
 * @property {string} [customerPhone]
 * @property {Array<{ description: string, quantity: number, rate: number, amount: number }>} items
 * @property {number} totalAmount
 * @property {string} [notes]
 */

import axios from '../api';
import { API_BASE } from '../config';

/**
 * Detect phones and tablets (desktop browsers keep open-in-tab behavior).
 */
export function isMobileOrTablet() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const mobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(ua);
  // iPadOS 13+ can report as MacIntel with touch points
  const iPadOs = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  const touchAndNarrow =
    ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 1) &&
    window.matchMedia('(max-width: 1024px)').matches;
  return mobileUa || iPadOs || touchAndNarrow;
}

/**
 * Open PDF in a new tab on desktop; force download on mobile/tablet
 * (blob URLs often fail when opened in a new tab on mobile browsers).
 * @param {Blob} blob
 * @param {string} [filename='document.pdf']
 */
export function openOrDownloadPdf(blob, filename = 'document.pdf') {
  const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
  const url = URL.createObjectURL(pdfBlob);

  if (isMobileOrTablet()) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Export quotation as PDF.
 * Desktop: opens in new tab. Mobile/tablet: downloads the file.
 * @param {number} groupId - Quotation group id (from quotation.id)
 */
export async function generateQuotationPdf(groupId) {
  try {
    const res = await axios.get(`${API_BASE}/api/quotations/${groupId}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    openOrDownloadPdf(blob, `quotation-${groupId}.pdf`);
  } catch (error) {
    const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to generate PDF';
    throw new Error(msg);
  }
}
